import { ChangeEvent, useEffect, useState } from "react";
import classNames from "classnames";
import { Add, CuToman, Delete } from "@payping/icons";
import { toEnDigit } from "@payping/utils/string";
import { Button, Input, InputNumber, LegacyForm, TableAction, Typography } from "@payping/web-components";
import { useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "./styles.module.scss";
import { SwitchRow } from "@/core/component";
import { buildUrl } from "@payping/utilities-shared/url";
import { URLS } from "@payping/utilities-shared/consts";
import { numberFormatter } from "@payping/utilities-shared/number";

const DonateScript = () => {
  const profilePicture = useAppSelector((state) => state.user.personalInfo?.profilePicture);
  const username = useAppSelector((state) => state.user.personalInfo?.username);
  const [description, setDescription] = useState<string>("");
  const [amounts, setAmounts] = useState<number[]>([]);
  const [script, setScript] = useState<string>("");
  const { t } = useAppTranslation();

  function selectAmount(amount: string) {
    const ppAmountInput = document.getElementById("ppDonateAmount") as HTMLInputElement;
    const realAmount = document.getElementById("ppRealAmount") as HTMLInputElement;
    ppAmountInput.value = amount;
    realAmount.value = toEnDigit(amount);
    ppAmountInput.focus();
    ppAmountInput.dispatchEvent(new Event("keyup", { bubbles: true }));
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setScript(`<script
                       src="${buildUrl(URLS.STORAGE_URL, "donate.min.js").toString()}"
                       type="text/javascript"
                       charset="utf-8"
                       pp-userName="${username}"
                       pp-description="${description}"
                       pp-amounts="${amounts.join(",")}"
                       pp-avatar="${profilePicture}"
                  ></script>`);

    const ppDescription = document.getElementById("ppDescription");
    const ppSelectAmount = document.getElementsByClassName("pp-amount")[0];
    if (ppDescription && ppSelectAmount) {
      const amountSection = amounts.map(
        (amount) =>
          `<div class="select-amount">
            <p id="${amount}">
              ${String(amount)} ${t("toman")}
            </p>
          </div>`,
      );
      ppSelectAmount.innerHTML = amountSection.join("");

      const enterTextElemet = document.getElementById("enterText");
      if (enterTextElemet) {
        enterTextElemet.innerHTML = t("services.detail.donate.addOrSelectAmount");
      }
      const amountBoxes = document.getElementsByClassName("select-amount");
      Array.prototype.slice.call(amountBoxes).forEach((_, index) => {
        const pAmountBox = amountBoxes[index].getElementsByTagName("p")[0];
        amountBoxes[index].addEventListener("click", (event): void => {
          if (event.target && (event.target as HTMLElement).children?.length > 0)
            selectAmount((event.target as HTMLElement).children[0].id);
        });
        pAmountBox.addEventListener("click", (event): void => {
          if (event.target) selectAmount((event.target as HTMLElement).id);
        });
      });

      ppDescription.innerHTML = description;
    }
  }, [description, amounts, username, profilePicture, t]);

  useEffect(() => {
    const s = document.createElement("script");
    s.src = buildUrl(URLS.STORAGE_URL, "donate.min.js").toString();
    s.async = true;
    if (username) s.setAttribute("pp-userName", username);
    s.setAttribute("pp-description", description);
    s.setAttribute("pp-amounts", amounts.join(","));
    if (profilePicture) s.setAttribute("pp-avatar", profilePicture);

    document.body.appendChild(s);

    return () => {
      document.body.removeChild(s);
      const ppDonate = document.getElementsByClassName("pp-donate")[0];
      ppDonate?.parentElement?.remove();
    };
    // After changing `amounts` and `description` values previous useEffect will rerun and apply the changes.
    // so we don't need these two variables in this dependency list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilePicture, username]);

  return (
    <LegacyForm layout={"vertical"} className="tw:w-full">
      <LegacyForm.Item label={t("description")} name={"description"} valuePropName={"children"}>
        <Input.TextArea
          placeholder={t("services.detail.donate.textAreaDescription")}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value)}
          value={description}
        />
      </LegacyForm.Item>
      <LegacyForm.Item valuePropName={"checked"} name={"hasSuggestedAmount"}>
        <SwitchRow
          label={t("services.detail.donate.suggestedAmount")}
          unCheckedChildren={t("inactive")}
          checkedChildren={t("active")}
        />
      </LegacyForm.Item>
      <LegacyForm.Item noStyle shouldUpdate={(prev, next) => prev.hasSuggestedAmount !== next.hasSuggestedAmount}>
        {(formInstance) =>
          formInstance.getFieldValue("hasSuggestedAmount") && (
            <>
              {amounts.map((value, index) => (
                <LegacyForm.Item
                  label={t("services.detail.donate.nthAmount", { count: index + 1 })}
                  className={styles.amountRow}
                  key={index}
                >
                  <InputNumber
                    formatter={(val) => numberFormatter.format(val, { maxDecimals: 0 })}
                    parser={(val) => numberFormatter.parse(val)}
                    className="tw:w-full!"
                    suffix={<CuToman width={24} height={24} />}
                    inputMode="numeric"
                    changeOnWheel={false}
                    controls={false}
                    value={value}
                    onChange={(val) => {
                      if (val) {
                        setAmounts((prev) => prev.map((v, i) => (i === index ? Number(val) : v)));
                      }
                    }}
                  />
                  <TableAction
                    Icon={Delete}
                    className={classNames("delete-btn")}
                    onClick={() => setAmounts((prev) => prev.filter((_, i) => i !== index))}
                  />
                </LegacyForm.Item>
              ))}
              <LegacyForm.Item>
                <Button
                  block
                  type={"dashed"}
                  icon={<Add width={14} height={14} />}
                  onClick={() => setAmounts((prev) => [...prev, 0])}
                >
                  {t("services.detail.donate.addAmount")}
                </Button>
              </LegacyForm.Item>
            </>
          )
        }
      </LegacyForm.Item>
      <LegacyForm.Item>
        <div className="tw:flex tw:justify-between">
          {t("script")} ({t("services.detail.donate.scriptCopyHelp")})
          <Typography.Text copyable={{ text: script }}></Typography.Text>
        </div>
        <Input.TextArea className="en-font ltr" rows={6} value={script} />
      </LegacyForm.Item>
    </LegacyForm>
  );
};

export default DonateScript;
